/**
 * The QuickBooks connection summary — the Invoices rail's status line.
 *
 * The SETTINGS live in Admin > Billing > QuickBooks: connect, disconnect,
 * the customer-link ceremony, the rate-band item map. What the billing
 * cockpit keeps is the ANSWER, in one line: which company is connected
 * and whether it is healthy.
 *
 * That answer has to be here because it is the first question a failed
 * push raises, and an operator who has to navigate to learn the
 * connection is broken has already been told the wrong thing by its
 * absence. So every unhealthy state says so in plain words and points at
 * the fix.
 *
 * It shares the card's query key, so React Query serves both surfaces
 * from ONE read of /qbo/status however many are mounted.
 *
 * The link is gated on canOpenQuickBooksSettings, the same union that
 * admits the view itself: a seat holding none of the QBO write
 * capabilities the view asks about (disconnect, connect or reconnect,
 * customer links) would land on a view coerced out from under it, so it
 * gets the state and no link.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canOpenQuickBooksSettings } from '@/lib/capabilities';
import { companyLabel, qboApi, type QboConnectionWire } from '@/lib/qboApi';
import { endSentence } from '@/lib/sentence';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface QboConnectionSummaryProps {
  /** Caller supplies the surrounding panel's classes (a panel's). */
  className?: string;
  /** 'band': one line on the page's forest header band (the Invoices
   *  page), cream text, and the company only by its name: a company with
   *  no stored name reads "Connected." rather than its realm number. */
  variant?: 'panel' | 'band';
}

/** One line. Short enough for a 322px rail, complete enough to act on. */
function summarySentence(connection: QboConnectionWire): string {
  if (connection.status === 'absent') return "QuickBooks isn't connected, so invoices can't be sent. Connect it in Admin, Billing, QuickBooks.";
  if (connection.status === 'multiple') {
    return "More than one QuickBooks company is connected, so nothing will send. Disconnect the one you don't use in Admin, Billing, QuickBooks.";
  }
  const who = companyLabel(connection.company_name, connection.realmId);
  if (connection.status === 'expired') {
    return `The QuickBooks link to ${who} has run out, so nothing will send. Reconnect it in Admin, Billing, QuickBooks.`;
  }
  return endSentence(`Connected to ${who}`);
}

/** The band's line (LP-34 round two): the state in one sentence, no
 *  route text, because the link beside it is the way there. The company
 *  only by its stored name, never its realm number. */
function bandSentence(connection: QboConnectionWire): string {
  if (connection.status === 'absent') return "QuickBooks isn't connected, so invoices can't be sent yet.";
  if (connection.status === 'multiple') {
    return 'More than one QuickBooks company is connected, so nothing will send.';
  }
  const name = connection.company_name;
  const who = name != null && name.trim() !== '' ? name : null;
  if (connection.status === 'expired') {
    return who === null
      ? 'The QuickBooks link has run out, so nothing will send.'
      : `The QuickBooks link to ${who} has run out, so nothing will send.`;
  }
  return who === null ? 'Connected.' : endSentence(`Connected to ${who}`);
}

/** The band link's words: the act the state asks for. */
function bandLinkLabel(connection: QboConnectionWire, unreadable: boolean): string {
  if (unreadable) return 'Open QuickBooks settings';
  if (connection.status === 'absent') return 'Connect QuickBooks';
  if (connection.status === 'expired') return 'Reconnect QuickBooks';
  return 'Open QuickBooks settings';
}

/** Healthy is the quiet case; everything else earns the alarm colour. */
function isHealthy(connection: QboConnectionWire): boolean {
  return connection.status === 'connected';
}

export function QboConnectionSummary({
  className,
  variant = 'panel',
}: QboConnectionSummaryProps = {}): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const canOpenSettings = canOpenQuickBooksSettings(whoami);
  const contact = useSupportContact();

  const statusQuery = useQuery({
    queryKey: ['qbo', 'status', tenantSlug],
    queryFn: ({ signal }) => qboApi.status(tenantSlug, signal),
  });

  // A status read that FAILED is not a healthy connection and must not
  // read like one. It is also not a disconnection, so it says what it
  // actually knows.
  const unreadable = statusQuery.isError;
  const connection: QboConnectionWire = statusQuery.data?.connection ?? { status: 'absent' };
  const sentence = statusQuery.isPending
    ? 'Checking the connection…'
    : unreadable
      ? contactSentence(
          "The QuickBooks connection couldn't be checked. Reload the page, and if it happens again, tell {contact_billing}.",
          contact,
        )
      : variant === 'band'
        ? bandSentence(connection)
        : summarySentence(connection);
  const quiet = statusQuery.isPending || (!unreadable && isHealthy(connection));

  if (variant === 'band') {
    // On the forest band the alarm colour does not read, so an unhealthy
    // state takes the weight, a mark and a tinted line instead, and keeps
    // its sentence.
    return (
      <p
        data-testid="qbo-connection-summary"
        data-quiet={quiet ? 'true' : 'false'}
        className={`flex min-w-0 max-w-full flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-cream/85 ${
          quiet ? '' : 'rounded-base bg-cream/10 px-3 py-1.5'
        }`}
      >
        <span className="text-label font-semibold uppercase tracking-label text-cream/70">QuickBooks</span>
        <span
          data-testid="qbo-summary-state"
          className={`min-w-0 break-words ${quiet ? '' : 'font-semibold text-cream'}`}
        >
          {!quiet && (
            <AlertTriangle
              aria-hidden="true"
              size={14}
              className="mr-1 inline-block align-[-2px] text-pepper-soft"
            />
          )}
          {sentence}
        </span>
        {canOpenSettings && (
          <Link
            data-testid="qbo-summary-link"
            to={`/t/${tenantSlug}/admin-review?tab=quickbooks`}
            className="text-cream underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream/70"
          >
            {statusQuery.isPending ? 'Open QuickBooks settings' : bandLinkLabel(connection, unreadable)}
          </Link>
        )}
      </p>
    );
  }

  return (
    <section data-testid="qbo-connection-summary" className={className}>
      <h2 className="text-title-sm font-semibold uppercase tracking-label text-sage-800">
        QuickBooks
      </h2>
      <p
        data-testid="qbo-summary-state"
        className={`mt-1 text-sm ${quiet ? 'text-ink-soft' : 'text-pepper'}`}
      >
        {sentence}
      </p>
      {canOpenSettings && (
        <Link
          data-testid="qbo-summary-link"
          to={`/t/${tenantSlug}/admin-review?tab=quickbooks`}
          className="mt-1 inline-block text-sm text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Open QuickBooks settings
        </Link>
      )}
    </section>
  );
}
